import axios from 'axios';
import { apiUrl, authApiUrl, authApiSecretAccessKey } from '@/env';
import {
  IUserProfile,
  IUserProfileUpdate,
  IUserProfileCreate,
  ITemplate,
  ITemplateCreate,
  IServiceCreate,
  IService,
  IServiceUpdate,
  ITemplateUpdate,
  IMessage,
  IMessageParseResult,
  IProxyError,
  IProxyUserBansStatistics,
  IProxyErrorCreate,
  IProxyErrorUpdate,
  IProxyUserBans,
  ISenderCreate,
  ISenderUpdate,
  ISender,
  IPriority,
  IPriorityUpdate,
  IProxyUserBanCreate,
  IMNO,
  IMNOUpdate,
  ITemplates,
  IMNOCreate,
  IMNOMaskCreate,
  IMNOMasks,
  IMNOSenderUpdate,
  IMNOSenderCreate,
  IMNOSender,
  ITag,
  IServiceBanPolicyCreateUpdate,
  IServiceBanPolicy,
  IVersion,
} from './interfaces';
import { ICheckTemplateProxy } from '@/interfaces/utilities';
import { IConfigResponse } from './interfaces/config';

export const api = {
  async logInGetToken(username: string, password: string) {
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);

    return axios.post(`${apiUrl}/api/v1/login/access-token`, params);
  },
  async getMe() {
    return axios.get<IUserProfile>(`${apiUrl}/api/v1/users/me`);
  },
  async updateMe(token: string, data: IUserProfileUpdate) {
    return axios.patch<IUserProfile>(`${apiUrl}/api/v1/users/me`, data);
  },
  async getUsers(token: string) {
    return axios.get<IUserProfile[]>(`${apiUrl}/api/v1/users/`);
  },
  async getUser(token: string, id: number) {
    return axios.get<IUserProfile>(`${apiUrl}/api/v1/users/${id}`);
  },
  async updateUser(token: string, userId: number, data: IUserProfileUpdate) {
    return axios.patch(`${apiUrl}/api/v1/users/${userId}`, data);
  },
  async createUser(token: string, data: IUserProfileCreate) {
    return axios.post(`${apiUrl}/api/v1/users/`, data);
  },
  async deleteUser(token: string, userId: number) {
    return axios.delete(`${apiUrl}/api/v1/users/${userId}`);
  },
  async deleteUsers(token: string, usersIds: number[]) {
    return axios.delete(`${apiUrl}/api/v1/users/delete/`, { data: usersIds });
  },

  async createTemplate(token: string, data: ITemplateCreate) {
    return axios.post(`${apiUrl}/api/v1/templates/`, data);
  },
  async updateTemplate(token: string, templateId: number, data: ITemplateUpdate) {
    return axios.patch(`${apiUrl}/api/v1/templates/${templateId}`, data);
  },
  async getTemplate(token: string, id: number) {
    return axios.get<ITemplate>(`${apiUrl}/api/v1/templates/${id}`);
  },
  async getTemplates(
    token: string,
    search: string,
    active: boolean | null,
    priorityId: number | null,
    senderId: number | null,
    serviceId: number | null,
    mnoBanId: number | null,
    tagId: number | null,
    skip: number,
    limit: number,
    orderBy: string,
    descending: boolean,
  ) {
    return axios.get<ITemplates>(`${apiUrl}/api/v1/templates/`, {
      params: {
        search,
        active,
        priority_id: priorityId,
        sender_id: senderId,
        service_id: serviceId,
        mno_ban_id: mnoBanId,
        tag_id: tagId,
        skip,
        limit,
        order_by: orderBy,
        descending,
      },
    });
  },
  async deleteTemplate(token: string, templateId: number) {
    return axios.delete(`${apiUrl}/api/v1/templates/${templateId}`);
  },
  async deleteTemplates(token: string, templateIds: number[]) {
    return axios.delete(`${apiUrl}/api/v1/templates/delete/`, { data: templateIds });
  },
  async parseMessage(token: string, payload: IMessage) {
    return axios.post<IMessageParseResult>(`${apiUrl}/api/v1/templates/parse_message`, payload);
  },

  async getContracts(token: string) {
    return axios.get(`${authApiUrl}/api/internal/contracts/`, { params: { secret: authApiSecretAccessKey } });
  },

  async createService(token: string, data: IServiceCreate) {
    return axios.post(`${apiUrl}/api/v1/services/`, data);
  },
  async getService(token: string, id: number) {
    return axios.get<IService>(`${apiUrl}/api/v1/services/${id}`);
  },
  async getServices(token: string) {
    return axios.get<IService[]>(`${apiUrl}/api/v1/services/`);
  },
  async updateService(token: string, serviceId: number, data: IServiceUpdate) {
    return axios.patch(`${apiUrl}/api/v1/services/${serviceId}`, data);
  },
  async deleteService(token: string, serviceId: number) {
    return axios.delete(`${apiUrl}/api/v1/services/${serviceId}`);
  },
  async deleteServices(token: string, serviceIds: number[]) {
    return axios.delete(`${apiUrl}/api/v1/services/delete/`, { data: serviceIds });
  },

  async createSender(token: string, data: ISenderCreate) {
    return axios.post(`${apiUrl}/api/v1/senders/`, data);
  },
  async getSender(token: string, id: number) {
    return axios.get<ISender>(`${apiUrl}/api/v1/senders/${id}`);
  },
  async getSenders(token: string) {
    return axios.get<ISender[]>(`${apiUrl}/api/v1/senders/`);
  },
  async updateSender(token: string, senderId: number, data: ISenderUpdate) {
    return axios.patch(`${apiUrl}/api/v1/senders/${senderId}`, data);
  },
  async deleteSender(token: string, senderId: number) {
    return axios.delete(`${apiUrl}/api/v1/senders/${senderId}`);
  },
  async deleteSenders(token: string, senderId: number[]) {
    return axios.delete(`${apiUrl}/api/v1/senders/delete/`, { data: senderId });
  },

  async createProxyError(token: string, data: IProxyErrorCreate) {
    return axios.post(`${apiUrl}/api/v1/proxy_errors/`, data);
  },
  async getProxyError(token: string, id: number) {
    return axios.get<IProxyError>(`${apiUrl}/api/v1/proxy_errors/${id}`);
  },
  async getProxyErrors(token: string, serviceId: number | null) {
    return axios.get<IProxyError[]>(`${apiUrl}/api/v1/proxy_errors/`, {
      params: { service_id: serviceId },
    });
  },
  async updateProxyError(token: string, proxyErrorId: number, data: IProxyErrorUpdate) {
    return axios.patch(`${apiUrl}/api/v1/proxy_errors/${proxyErrorId}`, data);
  },
  async deleteProxyError(token: string, proxyErrorId: number) {
    return axios.delete(`${apiUrl}/api/v1/proxy_errors/${proxyErrorId}`);
  },
  async deleteProxyErrors(token: string, proxyErrorsIds: number[]) {
    return axios.delete(`${apiUrl}/api/v1/proxy_errors/delete/`, {
      data: proxyErrorsIds,
    });
  },

  async createProxyUserBan(token: string, data: IProxyUserBanCreate) {
    return axios.post(`${apiUrl}/api/v1/proxy_user_bans/`, data);
  },
  async getProxyUserBans(
    token: string,
    skip: number,
    limit: number,
    orderBy: string,
    descending: boolean,
    phoneQuery: string,
  ) {
    return axios.get<IProxyUserBans>(`${apiUrl}/api/v1/proxy_user_bans/`, {
      params: { skip, limit, order_by: orderBy, descending, phone_query: phoneQuery },
    });
  },
  async deleteProxyUserBan(token: string, proxyUserBanId: number) {
    return axios.delete(`${apiUrl}/api/v1/proxy_user_bans/${proxyUserBanId}`);
  },
  async deleteProxyUserBans(token: string, proxyUserBanIds: number[]) {
    return axios.delete(`${apiUrl}/api/v1/proxy_user_bans/delete/`, {
      data: proxyUserBanIds,
    });
  },
  async getProxyUserBansStatistics(token: string) {
    return axios.get<IProxyUserBansStatistics[]>(`${apiUrl}/api/v1/proxy_user_bans/statistics`);
  },

  async getPriorities(token: string) {
    return axios.get<IPriority[]>(`${apiUrl}/api/v1/priorities/`);
  },
  async updatePriority(token: string, priorityId: number, data: IPriorityUpdate) {
    return axios.patch(`${apiUrl}/api/v1/priorities/${priorityId}`, data);
  },

  async checkTemplateProxy(token: string, data: ICheckTemplateProxy) {
    return axios.post(`${apiUrl}/api/v1/util/check_template_proxy`, data);
  },

  async getMNO(token: string, mnoId: number) {
    return axios.get(`${apiUrl}/api/v1/mnos/${mnoId}`);
  },
  async getMNOs(token: string) {
    return axios.get<IMNO[]>(`${apiUrl}/api/v1/mnos/`);
  },
  async updateMNO(token: string, mnoId: number, data: IMNOUpdate) {
    return axios.patch(`${apiUrl}/api/v1/mnos/${mnoId}`, data);
  },
  async createMNO(token: string, data: IMNOCreate) {
    return axios.post(`${apiUrl}/api/v1/mnos/`, data);
  },
  async deleteMNO(token: string, mnoId: number) {
    return axios.delete(`${apiUrl}/api/v1/mnos/${mnoId}`);
  },

  async getMNOMasks(token: string, skip: number, limit: number, mnoId: number) {
    return axios.get<IMNOMasks>(`${apiUrl}/api/v1/mnos/${mnoId}/masks/`, {
      params: { skip, limit },
    });
  },
  async checkMNOMask(token: string, phone: string) {
    return axios.get(`${apiUrl}/api/v1/util/check-mno-mask`, { params: { phone } });
  },
  async deleteMNOMasks(token: string, mnoId: number, ids: number[]) {
    return axios.delete(`${apiUrl}/api/v1/mnos/${mnoId}/masks/delete/`, { data: ids });
  },
  async createMNOMask(token: string, mnoId: number, data: IMNOMaskCreate) {
    return axios.post(`${apiUrl}/api/v1/mnos/${mnoId}/masks/`, data);
  },

  async getMNOSenders(token: string, mnoId: number) {
    return axios.get<IMNOSender[]>(`${apiUrl}/api/v1/mnos/${mnoId}/senders/`);
  },
  async deleteMNOSenders(token: string, mnoId: number, ids: number[]) {
    return axios.delete(`${apiUrl}/api/v1/mnos/${mnoId}/senders/delete/`, { data: ids });
  },
  async createMNOSender(token: string, mnoId: number, data: IMNOSenderCreate) {
    return axios.post(`${apiUrl}/api/v1/mnos/${mnoId}/senders/`, data);
  },
  async updateMNOSender(token: string, mnoId: number, mnoSenderId: number, data: IMNOSenderUpdate) {
    return axios.patch(`${apiUrl}/api/v1/mnos/${mnoId}/senders/${mnoSenderId}`, data);
  },

  async getTags(token: string) {
    return axios.get<ITag[]>(`${apiUrl}/api/v1/tags/`);
  },
  async suggestTags(token: string, text?: string) {
    return axios.get<ITag[]>(`${apiUrl}/api/v1/tags/`, { params: { text } });
  },

  async createServiceBanPolicy(token: string, data: IServiceBanPolicyCreateUpdate) {
    return axios.post(`${apiUrl}/api/v1/service-ban-policies/`, data);
  },
  async getServiceBanPolicy(token: string, id: number) {
    return axios.get<IServiceBanPolicy>(`${apiUrl}/api/v1/service-ban-policies/${id}`);
  },
  async getServiceBanPolicies(token: string) {
    return axios.get<IServiceBanPolicy[]>(`${apiUrl}/api/v1/service-ban-policies/`);
  },
  async updateServiceBanPolicy(token: string, serviceBanPolicyId: number, data: IServiceBanPolicyCreateUpdate) {
    return axios.patch(`${apiUrl}/api/v1/service-ban-policies/${serviceBanPolicyId}`, data);
  },
  async deleteServiceBanPolicy(token: string, serviceBanPolicyId: number) {
    return axios.delete(`${apiUrl}/api/v1/service-ban-policies/${serviceBanPolicyId}`);
  },
  async deleteServiceBanPolicies(token: string, serviceBanPolicyId: number[]) {
    return axios.delete(`${apiUrl}/api/v1/service-ban-policies/delete/`, {
      data: serviceBanPolicyId,
    });
  },
  async getVersion() {
    return axios.get<IVersion>(`${apiUrl}/api/v1/version/`);
  },
  async getConfig() {
    return axios.get<IConfigResponse>(`${apiUrl}/api/v1/config/`);
  },
};
